
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdZone, AdZoneCard } from "@/types/adZones";
import { cn } from "@/lib/utils";
import { getActiveFooterCards } from "@/utils/adZoneFooterUtils";

interface AdZoneFooterGuidesProps {
  zone: AdZone;
  className?: string;
}

const GuideCard = ({ card }: { card: AdZoneCard }) => (
  <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
    <div className="aspect-[3/4] w-full overflow-hidden bg-gray-100">
      {card.image_url ? (
        <img
          src={card.image_url}
          alt={card.title || "Guide pratique"}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">
          Image
        </div>
      )}
    </div>

    <div className="flex flex-1 flex-col gap-4 p-4 sm:p-5">
      {card.title && (
        <h3 className="text-base font-semibold leading-snug text-gray-800 sm:text-lg">
          {card.title}
        </h3>
      )}

      {card.cta_label && card.cta_url && (
        <Button
          asChild
          size="lg"
          className="mt-auto w-full rounded-full bg-[#23486a] text-white hover:bg-[#1a324d]"
        >
          <a href={card.cta_url} target="_blank" rel="noopener noreferrer">
            {card.cta_label}
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </Button>
      )}
    </div>
  </article>
);

const SectionTitle = () => (
  <h2 className="text-center text-2xl font-bold leading-tight text-gray-900 sm:text-3xl md:text-4xl">
    Guides Pratiques pour Entrepreneur.es
  </h2>
);

export const AdZoneFooterGuides = ({ zone, className }: AdZoneFooterGuidesProps) => {
  const cards = getActiveFooterCards(zone);

  if (cards.length === 0) {
    return null;
  }

  return (
    <section
      className={cn(
        "mt-10 border-t border-gray-100 bg-gradient-to-b from-[#f8f9fa] to-white py-10 sm:py-14",
        className
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle />

        {/* Desktop : grille 4 colonnes */}
        <div className="mt-8 hidden gap-5 md:grid md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {cards.map((card, index) => (
            <GuideCard key={`${card.title}-${index}`} card={card} />
          ))}
        </div>

        {/* Mobile : scroll horizontal avec amorce de la carte suivante */}
        <div
          className="mt-8 flex gap-4 overflow-x-auto pb-2 md:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory"
          aria-label="Guides pratiques — faire défiler horizontalement"
        >
          {cards.map((card, index) => (
            <div
              key={`${card.title}-mobile-${index}`}
              className="w-[82%] shrink-0 snap-start first:ml-0 last:mr-4 sm:w-[78%]"
            >
              <GuideCard card={card} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
